// PoScanPage — scan the purchase order out of the box, and stock what arrived.
//
// Mannie's framing, verbatim: "scan the PO out of the received box, and have it
// mint the item and fill in the quantity that was received."
//
// ── WHY THIS IS NOT THE IMPORT SCREEN ───────────────────────────────────────
// PoImportReview files four years of paperwork: many documents, a reconciliation
// workbook as the authority, a vendor triage panel that turns ~50 picks into
// ~15. Every one of those exists because that job is 193 documents long. This
// job is ONE document long, and the goods are on the desk while it happens, so
// the screen is built for the opposite thing: fewest possible actions between
// dropping the file and the stock being real.
//
// Drop to stocked, for a three-line document with two items nobody has
// cataloged: DROP, then CONFIRM. Two actions. The SKUs arrive filled in, the
// build stage arrives preselected (everything on a purchase order was bought,
// which is what raw material means), and the item type arrives preselected
// whenever the line's own words justify it. Anything the screen could not
// justify is left empty and blocks Confirm by name, which is the one place this
// flow deliberately spends a click.
//
// The engine underneath is shared with the import lane, not copied: same
// parser, same AI grounding, same needs-review gate, same cross-instance
// refusal. See api/src/procurement/po-scan-service.js.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import HelpDrawer from '../components/HelpDrawer';
import { formatCents } from './poApi';
import {
  isAiSource,
  reviewKey,
  PRODUCT_LABEL,
  foreignEntityFor,
  type ImportSource,
  type ReviewRequiredEntry,
  type ReviewResolution,
  type ConfirmReviewInput,
} from './poImportApi';
import { slotLabel } from '../components/review/grounding';
import { ConfidenceDot } from '../components/review/confidence';
import { laneLabel as labelClass } from '../components/review/fieldStyles';
import { Checkbox } from '../components/ui/Checkbox';
import { Field, fieldClass } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';
import { ReviewNote, type ReviewSlot } from '../components/review/ReviewNote';
import { ConfirmBar } from '../components/review/ConfirmBar';
import {
  usePoScanApi,
  type ScanParseResponse,
  type ScanConfirmResult,
  type ScanConfirmLineInput,
  type ScanDraftSummary,
  type ScanDraftFile,
} from './poScanApi';
import ScanDraftsPanel, { ScanDraftPages } from './PoScanDrafts';
import PoScanLineCard, {
  boardMint,
  draftMint,
  type ScanLineDraft,
  type ScanLineError,
} from './PoScanLineCard';
// The vendor form the Vendors admin page uses, mounted here as a drawer so a
// board line whose maker nobody has entered does not send the reviewer away.
import { VendorForm, type VendorFullRow } from '../Admin/VendorsAdmin';
import { MAX_SCAN_PAGES, SCAN_PHOTO_ACCEPT, isScanPhoto, shrinkPhoto } from './scanPhotos';
import { skuIsWellFormed } from '../Parts/SkuBuilder';
import useItemKindsStore, { type ItemKind } from '../stores/useItemKindsStore';
import useCodedVendors, { codeOf, type CodedVendor } from '../Parts/useCodedVendors';
import type { MaterialType, CommodityClass, ReceivedAs } from '../Receiving/types';
import ReceiverIdentityModal from '../Receiving/ReceiverIdentityModal';
import { receiverErrorMessage } from '../Receiving/receivedAs';
import type { VendorTypeOption } from './vendorTypeLabels';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import PageLayout from '../components/ui/PageLayout';
import DropZone from '../components/ui/DropZone';
import { FilePick } from '../components/ui/FilePick';
import { Panel } from '../components/ui/Panel';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { IconChevronLeft, IconChevronRight, IconClose } from '../components/ui/icons';

/**
 * Mirrors PO_NUMBER_RE in api/src/procurement/po-import-xlsx.js — the server is
 * the authority, this is here to stop a silent surprise.
 *
 * canonicalPoNumber() accepts ONLY these two shapes, and when the typed value
 * does not match, _validateDocument quietly falls back to the number parsed out
 * of the FILENAME. In the import lane that fallback is a feature (24 of 193 real
 * documents print a number that disagrees with their filename). Here it would
 * throw away the one correction this screen exists to invite: a reviewer fixing
 * a misread PO number, fat-fingering it to four digits, and getting the original
 * misread filed anyway.
 */
const PO_NUMBER_SHAPE = /^PO-(?:FF-TEC|SDTEC-)\d{5}$/i;

interface VendorOption {
  id: string;
  name: string;
  type: string;
  is_disabled: boolean;
}

// The id of the "create it" row in the vendor picker. The create action lives in
// the list as its own row rather than beside the field, the way Odoo's many2one
// carries its quick-create among the matches. vendors.id is a uuid and no uuid
// contains an underscore, so this can never collide with a real vendor.
const CREATE_NEW_VENDOR = '__create_new_vendor__';

type Phase = 'idle' | 'parsing' | 'review' | 'done';

/** One photographed page, waiting to be sent. `url` is an object URL for the thumbnail. */
interface PhotoPage {
  key: string;
  file: File;
  url: string;
}

let photoKeySeq = 0;

/**
 * Turn a staged document into the screen's working copy.
 *
 * Everything the server proposed is a starting point a person can overwrite;
 * nothing here is final until Confirm.
 *
 * A new line arrives KIND-FIRST (`draftMint`): the description's own type token
 * is looked up in the taxonomy, so "M3x8 socket screw" opens knowing it is a
 * screw and needing only a name, while a board-shaped guess opens on the SKU
 * builder with its segments already filled. A token nothing recognizes leaves
 * the kind unanswered and the card asks, which is the one thing a document lane
 * must never guess: a wrong kind is a wrong code, minted forever.
 *
 * `kinds` is passed rather than read off the store here, because a pure
 * function of the document plus the taxonomy is a pure function; the caller
 * awaits the load before hydrating.
 */
function draftLinesFrom(parsed: ScanParseResponse, kinds: ItemKind[]): ScanLineDraft[] {
  const extraction = parsed.document?.extraction;
  if (!extraction) return [];

  return extraction.lines.map((line, index) => {
    const matched = line.part_catalog_id !== null && line.part_catalog_id !== undefined;
    const description = line.description || '';
    let mint: ScanLineDraft['mint'] = null;

    if (!matched && line.line_type === 'stock' && line.included !== false) {
      mint = draftMint({
        description,
        kinds,
        // The reader's own code proposal on a line it could not match. When it
        // is board-shaped it names a kind, which is better than a token guess.
        skuGuess: line.sku_guess,
        // 2026-08-07. This lane could not carry item identity at all before, so
        // every SKU it minted had nobody's name on it. Both prefilled from the
        // document and both clearable.
        partNumber: line.part_number,
        manufacturer: line.manufacturer,
      });
    }

    return {
      key: `line-${index}`,
      lineNumber: line.line_number || index + 1,
      description,
      // Carried whether or not this line mints, so switching a matched line to
      // "create a new item" reuses the read instead of starting blank.
      partNumber: line.part_number,
      manufacturer: line.manufacturer,
      quantity: line.quantity,
      unitCostCents: line.unit_cost_cents,
      unitPriceText: line.unit_price_text,
      unitPriceExact: line.unit_price_exact !== false,
      lineType: line.line_type,
      included: line.included !== false,
      partCatalogId: matched ? line.part_catalog_id : null,
      matchedSku: matched ? line.sku_guess : null,
      mint,
    };
  });
}

/** The first thing blocking Confirm on this line, or null. */
/**
 * @param knownCodes every vendor code that exists, retired ones included, as
 *   SKU segments spell them; null until the list has loaded, when the segment
 *   is the reviewer's word.
 */
function lineError(line: ScanLineDraft, knownCodes: Set<string> | null): ScanLineError | null {
  if (!line.included || line.lineType !== 'stock') return null;
  if (!line.mint) {
    if (!line.partCatalogId) {
      return { field: 'sku', message: 'Point this line at an item, or create a new one.' };
    }
    return null;
  }
  // THE KIND IS THE FIRST QUESTION AND THE FIRST GATE. Everything else about a
  // new item depends on the answer, and nothing here may invent one.
  if (!line.mint.kindKey) {
    return {
      field: 'kind',
      message: `Line ${line.lineNumber} needs to say what sort of thing it is before it can be created.`,
    };
  }
  if (line.mint.board) {
    // A board's code is composed, and an incomplete one is refused rather than
    // minted short: the server parses those segments back into a part family.
    if (!skuIsWellFormed('board', line.mint.sku || '')) {
      return {
        field: 'sku',
        message: 'Finish the board code. Rev major and manufacturer code are both needed.',
      };
    }
    // The segment has to name somebody. A code nobody carries mints a board
    // with no assembler and says nothing. Same gate as the quote lane.
    const mfr = line.mint.board.vendorCode;
    if (knownCodes && knownCodes.size > 0 && mfr && !knownCodes.has(mfr)) {
      return {
        field: 'sku',
        message: `${mfr} is nobody's code. Pick the maker, or add them with New vendor….`,
      };
    }
  } else if (!line.mint.name.trim()) {
    // A short code says nothing about the thing, so the name is the only thing
    // any other screen will have to show.
    return { field: 'name', message: 'Give this new item a name. It is what every screen will show.' };
  }
  if (!line.mint.materialType) {
    return { field: 'material_type', message: 'Pick a build stage. Nothing guesses it for you.' };
  }
  if (!line.mint.commodityClass) {
    return { field: 'commodity_class', message: 'Pick an item type. Nothing guesses it for you.' };
  }
  return null;
}

const PoScanPage: React.FC = () => {
  const api = useApi();
  const scanApi = usePoScanApi();
  const toast = useToast();
  const history = useHistory();

  // The taxonomy a new line's kind is guessed against, and the assemblers a
  // board line's manufacturer segment can name. Both are read here and handed
  // down: a document is thirty cards, and thirty fetches of one list is thirty
  // fetches of one list.
  const kinds = useItemKindsStore((s) => s.kinds);
  const fetchedVendors = useCodedVendors(api);
  // Vendors created from a line card's "New vendor…" on this screen. They are
  // in the database already; this keeps them in the list without a refetch.
  const [addedVendors, setAddedVendors] = useState<CodedVendor[]>([]);
  // Everyone with a code, retired included, for judging a code on a SKU; the
  // active ones for offering. See useCodedVendors on why the two differ.
  const knownCodedVendors = useMemo(
    () => [...fetchedVendors, ...addedVendors.filter((a) => !fetchedVendors.some((f) => f.id === a.id))],
    [fetchedVendors, addedVendors]
  );
  const knownCodes = useMemo(
    () => (knownCodedVendors.length ? new Set(knownCodedVendors.map(codeOf)) : null),
    [knownCodedVendors]
  );
  const codedVendors = knownCodedVendors;
  // Which line's manufacturer segment opened the vendor drawer, or null.
  const [vendorDrawerFor, setVendorDrawerFor] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>('idle');
  const [parsed, setParsed] = useState<ScanParseResponse | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const [poNumber, setPoNumber] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [lines, setLines] = useState<ScanLineDraft[]>([]);
  const [goodsInHand, setGoodsInHand] = useState(true);
  // B7's control: the quantities above go into stock as they read, so a human
  // says out loud that they match the physical box. Defaults unchecked on
  // every new review; the API refuses a stocking confirm without it.
  const [countsAffirmed, setCountsAffirmed] = useState(false);
  const [reviews, setReviews] = useState<Record<string, ReviewResolution>>({});
  // "Yes, this really is the other product's purchase order." Only consulted when
  // the staged document carries a foreign_entity. Starts false: this lane puts
  // stock on a shelf in the same request, so the opt-in is the whole point.
  const [foreignAck, setForeignAck] = useState(false);
  // Stocking asks who is at the bench first; paperwork-only confirms do not.
  const [identityOpen, setIdentityOpen] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);

  const [vendorList, setVendorList] = useState<VendorOption[]>([]);
  const [vendorTypes, setVendorTypes] = useState<VendorTypeOption[]>([]);
  const [vendorId, setVendorId] = useState<string>('');
  const [vendorCreateName, setVendorCreateName] = useState('');
  const [vendorCreateType, setVendorCreateType] = useState('');

  // ── the holding cell ────────────────────────────────────────────────────
  // The scan being reviewed is ALWAYS a staged draft now: parse writes one, and
  // reviewing an older one from the list picks the same road up further along.
  // draftId is what Confirm clears, and what the page strip reads from.
  const [draftId, setDraftId] = useState<number | null>(null);
  const [draftFiles, setDraftFiles] = useState<ScanDraftFile[]>([]);
  const [drafts, setDrafts] = useState<ScanDraftSummary[]>([]);
  const [draftBusyId, setDraftBusyId] = useState<number | null>(null);

  const [photos, setPhotos] = useState<PhotoPage[]>([]);
  // Pages already claimed, INCLUDING ones still being shrunk. State cannot hold
  // this: it only updates on re-render, and the shrink is awaited.
  const queuedCountRef = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanConfirmResult | null>(null);
  const fileInputId = 'po-scan-file';
  const photoInputId = 'po-scan-photo';
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = (await api.get('/procurement/vendors')) as {
          vendors: VendorOption[];
          // Labels included, active only, already in order. An admin edits the
          // taxonomy at /admin/vendor-types, so this page holds no copy of it.
          types: VendorTypeOption[];
        };
        setVendorList(data.vendors || []);
        setVendorTypes(data.types || []);
      } catch {
        // The vendor picker degrades to "type a new one"; not fatal.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doc = parsed?.document || null;
  const extraction = doc?.extraction || null;

  /** Everything waiting for review. Communal, so this is not "mine". */
  const refreshDrafts = useCallback(async () => {
    try {
      const data = await scanApi.listDrafts();
      setDrafts(data.drafts || []);
    } catch {
      // The list is a convenience on top of a flow that works without it.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Put a staged reading on the review surface.
   *
   * ONE road, whether the read just happened at the bench or was picked up from
   * the holding cell an hour later — a staged document is a staged document,
   * and a second hydration path is how the desk review would quietly become a
   * different review from the bench one.
   *
   * Returns false when the document is not reviewable, having set the message.
   */
  const hydrate = useCallback(
    (data: ScanParseResponse, id: number | null, files: ScanDraftFile[]): boolean => {
      const document = data.document;
      if (!document || document.status === 'error' || !document.extraction) {
        setParseError(document?.error?.message || 'That file could not be read as a purchase order.');
        return false;
      }
      setParsed(data);
      setDraftId(id);
      setDraftFiles(files);
      setPoNumber(document.extraction.po_number || '');
      setOrderDate(document.extraction.order_date || data.today);
      // getState, not the subscribed `kinds`: hydrate is a stable callback and
      // would close over the array as it stood at mount. Both callers await the
      // load first, so this is the list that just arrived.
      setLines(draftLinesFrom(data, useItemKindsStore.getState().kinds));
      setReviews({});
      setForeignAck(false);
      // Same family as foreignAck: a per-document statement. Carrying it from
      // the last box would pre-affirm counts nobody counted.
      setCountsAffirmed(false);
      const resolution = document.vendor_resolution;
      setVendorId(resolution?.matched_vendor_id || '');
      setVendorCreateName(resolution?.create_proposal?.name || document.extraction.vendor?.name || '');
      setVendorCreateType('');
      setPhase('review');
      return true;
    },
    []
  );

  /**
   * Send one purchase order to be read: a single file, or the photographed
   * pages in the order they are on screen. One call either way, because it is
   * one purchase order either way.
   *
   * The read is STAGED server-side before it comes back, so from here on the
   * work survives a locked phone, a closed tab and a walk to the desk. The
   * pages shown from here on are the STAGED ones rather than the local object
   * URLs: one source, and it is the same one a desk review would see.
   */
  const parseFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setPhase('parsing');
    setParseError(null);
    try {
      const data = await scanApi.parse(files);
      // The kinds decide what every mint card opens as, so the list has to be
      // here before the lines are drafted. Cached after the first document, and
      // a failure is recorded rather than thrown: the cards then all ask.
      await useItemKindsStore.getState().load(api);
      const stagedId = data.draft_id ?? null;
      const staged: ScanDraftFile[] = stagedId
        ? files.map((file, index) => ({
          page_number: index + 1,
          mimetype: file.type || 'application/octet-stream',
          size_bytes: file.size,
        }))
        : [];
      if (!hydrate(data, stagedId, staged)) setPhase('idle');
      refreshDrafts();
    } catch (err: any) {
      setParseError(err?.data?.error?.message || err?.message || 'That file could not be read.');
      setPhase('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrate, refreshDrafts]);

  /** Pick a staged scan back up, wherever it was left. */
  const reviewDraft = useCallback(
    async (id: number) => {
      setDraftBusyId(id);
      setParseError(null);
      try {
        const draft = await scanApi.getDraft(id);
        // Same reason as the parse path: the mint cards cannot be drafted
        // against a taxonomy that has not arrived.
        await useItemKindsStore.getState().load(api);
        clearPhotos();
        hydrate(draft.staged_doc, draft.id, draft.files);
      } catch (err: any) {
        setParseError(
          err?.data?.error?.message || err?.message || 'That scan could not be opened. It may have been filed already.'
        );
        refreshDrafts();
      } finally {
        setDraftBusyId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hydrate, refreshDrafts]
  );

  /** A mis-snap. The confirmation happens in the panel, next to the scan. */
  const discardDraft = useCallback(
    async (id: number) => {
      setDraftBusyId(id);
      try {
        await scanApi.discardDraft(id);
        setDrafts((prev) => prev.filter((d) => d.id !== id));
        toast.success('Scan discarded.');
      } catch (err: any) {
        toast.error(err?.message || 'That scan could not be discarded.');
        refreshDrafts();
      } finally {
        setDraftBusyId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshDrafts]
  );

  /**
   * Take whatever was dropped or picked and do the right thing with it.
   *
   * Photos QUEUE — they are pages of one order, and the person is still taking
   * them. Anything else parses immediately, because a .pdf or a .xlsx is the
   * whole document already.
   */
  const acceptFiles = useCallback(
    async (list: FileList | File[] | null | undefined) => {
      const files = Array.from(list || []);
      if (files.length === 0) return;

      if (!files.every(isScanPhoto)) {
        // A mixed drop is a slip, not an intent. Reading the first file and
        // silently dropping the rest is how a two-page order gets filed with
        // one page of lines.
        if (files.length > 1) {
          setParseError('Drop one file, or photos of the pages of one purchase order. A PDF or a spreadsheet has to come on its own.');
          return;
        }
        clearPhotos();
        await parseFiles(files);
        return;
      }

      setParseError(null);
      // Claimed against the COUNTER, not against state, and claimed before the
      // await. `photos.length` is a render-time value: pick again while the
      // first shrink is still running and both calls see the same room, and 8
      // pages become 16 with no message until the server refuses the read.
      const room = MAX_SCAN_PAGES - queuedCountRef.current;
      if (room <= 0) {
        setParseError(`That is all ${MAX_SCAN_PAGES} pages. Remove one to add another.`);
        return;
      }
      const taken = files.slice(0, room);
      queuedCountRef.current += taken.length;
      // Shrunk before they are queued, so the thumbnail, the page count and the
      // upload are all talking about the same bytes.
      const shrunk = await Promise.all(taken.map(shrinkPhoto));
      // The blob URLs are minted HERE, not inside the updater: an updater has
      // to be pure, and one that React replays would mint a second URL per page
      // that never reaches state and is therefore never revoked.
      const added: PhotoPage[] = shrunk.map((file) => {
        photoKeySeq += 1;
        return { key: `photo-${photoKeySeq}`, file, url: URL.createObjectURL(file) };
      });
      setPhotos((prev) => [...prev, ...added]);
      if (taken.length < files.length) {
        setParseError(`Only ${MAX_SCAN_PAGES} pages fit in one purchase order, so the rest were left out.`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parseFiles]
  );

  // Revoking lives in the handlers, never inside a setState updater: an updater
  // has to be pure, and React is free to run it twice.
  const removePhoto = (key: string) => {
    const gone = photos.find((p) => p.key === key);
    if (!gone) return;
    URL.revokeObjectURL(gone.url);
    queuedCountRef.current = Math.max(0, queuedCountRef.current - 1);
    setPhotos((prev) => prev.filter((p) => p.key !== key));
  };

  /** Move a page one place earlier or later. Page order is reading order. */
  const movePhoto = (key: string, delta: -1 | 1) =>
    setPhotos((prev) => {
      const from = prev.findIndex((p) => p.key === key);
      const to = from + delta;
      if (from === -1 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });

  // Object URLs are held by the browser until told otherwise; leaving them
  // behind pins every photo taken this session in memory. The ref is what makes
  // the unmount cleanup run ONCE — an effect that depended on `photos` would
  // revoke the URLs of the thumbnails still on screen every time a page was
  // added — and it also gives clearPhotos the current list without taking a
  // dependency on render order.
  const photosRef = useRef<PhotoPage[]>([]);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  const clearPhotos = () => {
    photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    queuedCountRef.current = 0;
    setPhotos([]);
  };

  useEffect(() => {
    if (phase === 'review') firstFieldRef.current?.focus();
  }, [phase]);

  const patchLine = (key: string, patch: Partial<ScanLineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const patchMint = (key: string, patch: Partial<NonNullable<ScanLineDraft['mint']>>) =>
    setLines((prev) =>
      prev.map((l) => (l.key === key && l.mint ? { ...l, mint: { ...l.mint, ...patch } } : l))
    );

  // Switching a MATCHED line to "create a new item" reuses the read's own
  // suggestions rather than starting blank: the person doing it is the one who
  // just noticed the match was wrong, which is exactly when they deserve the
  // help. Same guesses, same helper, as a line that arrived unmatched.
  const switchToMint = (key: string) =>
    setLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? {
            ...l,
            partCatalogId: null,
            matchedSku: null,
            matchedItem: null,
            mint: draftMint({
              description: l.description,
              kinds,
              partNumber: l.partNumber,
              manufacturer: l.manufacturer,
            }),
          }
          : l
      )
    );

  const switchToMatch = (key: string) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, mint: null } : l)));

  // ── the needs-review gate, carried over unchanged ───────────────────────
  // Not the kit's groupSlots: this screen wants an ARRAY rather than a map. The
  // ring is the same amber the other lanes put on a flagged input — one gate,
  // one look, on whichever screen a reviewer happens to be standing in front of.
  const reviewEntries: ReviewRequiredEntry[] = doc?.review_required || [];
  const reviewGroups = useMemo(() => {
    const map = new Map<string, ReviewRequiredEntry[]>();
    for (const entry of reviewEntries) {
      const key = reviewKey(entry);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(entry);
    }
    return map;
  }, [reviewEntries]);

  const reviewSlots: ReviewSlot[] = useMemo(
    () =>
      [...reviewGroups.entries()].map(([key, entries]) => ({
        key,
        entries,
        open: !reviews[key],
        label: slotLabel(entries[0].target, entries[0].field, entries[0].line_number),
        // Leading space: appended straight onto an input's className. A RING,
        // not a border: the fields are filled now and carry no outline for an
        // amber one to override, and a ring occupies no layout so a flagged
        // field does not nudge the grid it sits in.
        ring: reviews[key] ? '' : ' ring-2 ring-amber-500/60',
        markEdited: () => setReviews((prev) => ({ ...prev, [key]: 'edited' })),
        looksRight: () => setReviews((prev) => ({ ...prev, [key]: 'confirmed_as_staged' })),
      })),
    [reviewGroups, reviews]
  );
  const unreviewed = reviewSlots.filter((s) => s.open).length;

  /**
   * The slot for one header field, or null when the reader had no doubt there.
   *
   * Every field the gate can raise MUST be rendered next to its own slot. The
   * gate's whole premise is that a person looked at the value; a "Looks right"
   * button beside a field the screen never shows is an affirmation of something
   * nobody saw, which is the "gate turns into a button people mash" failure the
   * shared component's own comment warns about. So the fields the document
   * carries but this flow does not ask anyone to edit are rendered read-only
   * rather than dropped.
   */
  const slotFor = (field: string): ReviewSlot | null =>
    reviewSlots.find((s) => s.key === reviewKey({ target: 'field', field, line_number: null })) || null;

  // Anchored = shown beside its own value below. Everything else falls to the
  // panel, which is the honest home for document-level and line-level doubts.
  const ANCHORED_FIELDS = ['po_number', 'order_date', 'vendor', 'vendor.name', 'quote_number',
    'ship_date', 'ship_via', 'terms', 'ship_to', 'subtotal_cents'];
  const anchoredKeys = new Set(
    ANCHORED_FIELDS.map((f) => reviewKey({ target: 'field', field: f, line_number: null }))
  );
  const looseSlots = reviewSlots.filter((s) => !anchoredKeys.has(s.key));

  const includedLines = lines.filter((l) => l.included);
  const errorsByKey = useMemo(() => {
    const map = new Map<string, ScanLineError>();
    for (const line of lines) {
      const err = lineError(line, knownCodes);
      if (err) map.set(line.key, err);
    }
    return map;
  }, [lines, knownCodes]);

  // The vendor picker's rows: the create action first, marked `new`, then every
  // vendor. The action names the vendor the DOCUMENT named, not whatever is in
  // the New vendor name field — that field is editable, and a row whose label
  // changes per keystroke would rewrite the closed field under the person typing.
  // `Retired` is where an is_disabled vendor's "(retired)" suffix went.
  const vendorPickerOptions = useMemo<ComboboxOption[]>(
    () => [
      {
        id: CREATE_NEW_VENDOR,
        label: extraction?.vendor?.name
          ? `Create ${extraction.vendor.name} as new vendor`
          : 'Create a new vendor',
        chip: 'new',
      },
      ...vendorList.map((v) => ({
        id: v.id,
        label: v.name,
        sublabel: v.is_disabled ? 'Retired' : null,
      })),
    ],
    [extraction, vendorList]
  );

  const linesTotalCents = includedLines.reduce(
    (sum, l) => sum + (l.unitCostCents || 0) * l.quantity,
    0
  );
  const printedSubtotal = extraction?.subtotal_cents ?? null;

  // The header fields this flow does not ask anyone to edit, but the document
  // may carry and the needs-review gate may flag. Shown, so a "Looks right" is
  // about something the reviewer can actually see.
  const DOC_FIELDS: { field: string; label: string; value: string | null }[] = [
    { field: 'quote_number', label: 'Quote', value: extraction?.quote_number || null },
    { field: 'ship_date', label: 'Ship date', value: extraction?.ship_date || null },
    { field: 'ship_via', label: 'Ship via', value: extraction?.ship_via || null },
    { field: 'terms', label: 'Terms', value: extraction?.terms || null },
    {
      field: 'ship_to',
      label: 'Ship to',
      value: [extraction?.ship_to_name, extraction?.ship_to_attn, extraction?.ship_to_address]
        .filter(Boolean).join(', ') || null,
    },
  ];
  const mintCount = includedLines.filter((l) => l.mint).length;
  const stockCount = includedLines.filter((l) => l.lineType === 'stock').length;

  const vendorMatched = !!vendorId;
  const vendorReady = vendorMatched || (vendorCreateName.trim() && vendorCreateType);
  const blockers: string[] = [];
  if (!poNumber.trim()) blockers.push('a purchase-order number');
  else if (!PO_NUMBER_SHAPE.test(poNumber.trim())) blockers.push('a purchase-order number in the usual shape');
  if (!orderDate) blockers.push('an order date');
  if (includedLines.some((l) => !Number.isInteger(l.quantity) || l.quantity <= 0)) {
    blockers.push('a quantity above zero on every line');
  }
  if (!vendorReady) blockers.push('a vendor');
  if (includedLines.length === 0) blockers.push('at least one line');
  if (errorsByKey.size > 0) blockers.push(`${errorsByKey.size} line${errorsByKey.size === 1 ? '' : 's'} to finish`);
  if (unreviewed > 0) blockers.push(`${unreviewed} thing${unreviewed === 1 ? '' : 's'} to look at`);
  // From the FIELD, not from the staged read. The number above is editable and
  // correcting a misread prefix is a plausible edit, so the server re-derives this
  // from whatever gets submitted; deciding from the staged answer left Confirm
  // enabled on a document the server would refuse.
  const foreignEntity = parsed ? foreignEntityFor(poNumber, parsed.instance) : null;
  const foreignWarning = (doc?.warnings || []).find((w) => w.code === 'foreign_instance_po')?.message;
  if (foreignEntity && !foreignAck) {
    blockers.push(`a yes on the ${PRODUCT_LABEL[foreignEntity]} order above`);
  }
  if (goodsInHand && !countsAffirmed) {
    blockers.push('the quantity check below');
  }
  const canConfirm = blockers.length === 0 && !submitting;

  const handleConfirm = () => {
    if (!canConfirm || !doc || !extraction) return;
    if (goodsInHand) {
      setIdentityError(null);
      setIdentityOpen(true);
      return;
    }
    submitConfirm(null);
  };

  const submitConfirm = async (receivedAs: ReceivedAs | null) => {
    if (!canConfirm || !doc || !extraction) return;
    setSubmitting(true);
    setSubmitError(null);

    const payloadLines: ScanConfirmLineInput[] = includedLines.map((l) => ({
      description: l.description || null,
      quantity: l.quantity,
      line_type: l.lineType,
      unit_cost_cents: l.unitCostCents,
      // Only when the printed price did not land exactly on a cent: the server
      // appends "[imported unit price X, rounded to Y cents]" to the line so the
      // digits are not lost to an integer column.
      ...(l.unitPriceText && !l.unitPriceExact
        ? { unit_price_text: l.unitPriceText, unit_price_exact: false }
        : {}),
      part_catalog_id: l.mint ? null : l.partCatalogId,
      // The empty-string members are unreachable here: lineError() blocks
      // Confirm until both axes are answered, which is the whole point of that
      // gate. The cast says so rather than inventing a fallback value, because a
      // fallback would be exactly the silent default migration 016 removed.
      mint: l.mint
        ? {
          // The kind is what tells the server which code to make. A board
          // carries the composed string it was built from; a short kind carries
          // NO sku at all and the server assigns STEM-XXXX inside the same
          // transaction. Sending an empty string instead would be asking it to
          // mint a code nobody chose.
          kind_key: l.mint.kindKey as string,
          name: l.mint.name.trim() || null,
          ...(l.mint.board ? { sku: (l.mint.sku || '').trim() } : {}),
          material_type: l.mint.materialType as MaterialType,
          commodity_class: l.mint.commodityClass as CommodityClass,
          description: l.description || null,
          mpn: (l.mint.mpn || '').trim() || null,
          manufacturer: (l.mint.manufacturer || '').trim() || null,
        }
        : null,
    }));

    // Only the places a person actually answered. Confirm is blocked while any
    // are open, so this is the whole list — and if that guard is ever wrong, the
    // server recomputing the list is what catches it. Filling in a resolution
    // nobody gave would defeat exactly that.
    const payloadReviews: ConfirmReviewInput[] = [...reviewGroups.entries()]
      .filter(([key]) => reviews[key])
      .map(([key, entries]) => ({
        target: entries[0].target,
        field: entries[0].field,
        line_number: entries[0].line_number,
        resolution: reviews[key],
      }));

    try {
      const confirmed = await scanApi.confirm(
        {
          source_filename: doc.source_filename,
          source: (doc.source || 'xlsx') as ImportSource,
          po_number: poNumber.trim(),
          // Every scanned order was placed with the vendor before the box could
          // arrive, so it lands submitted. Receiving is what moves it on from
          // there, through the same recompute every other receive uses.
          status: 'submitted',
          order_date: orderDate,
          quote_number: extraction.quote_number,
          ship_date: extraction.ship_date,
          ship_via: extraction.ship_via,
          terms: extraction.terms,
          ship_to_name: extraction.ship_to_name,
          ship_to_attn: extraction.ship_to_attn,
          ship_to_address: extraction.ship_to_address,
          // The subtotal the DOCUMENT prints, not the sum of the lines. When
          // they disagree (tax, a shipping figure the parser did not emit as its
          // own line) the server keeps this one and writes the difference into
          // the header notes. Omitting it would silently replace the paper
          // figure with our arithmetic and record no disagreement at all.
          ...(extraction.subtotal_cents !== null && extraction.subtotal_cents !== undefined
            ? { subtotal_cents: extraction.subtotal_cents }
            : {}),
          vendor_id: vendorMatched ? vendorId : null,
          vendor_create: vendorMatched
            ? null
            : { name: vendorCreateName.trim(), type: vendorCreateType },
          vendor_name: vendorMatched
            ? vendorList.find((v) => v.id === vendorId)?.name || null
            : vendorCreateName.trim(),
          lines: payloadLines,
          attach_line_ids: [],
          // From the same derivation the screen blocked on, so the flag and the
          // number in this payload can never disagree.
          ...(foreignEntity ? { foreign_instance_ack: foreignAck } : {}),
          ...(isAiSource(doc.source)
            ? {
              staged_extraction: extraction,
              staged_confidence: doc.confidence,
              staged_warnings: doc.warnings,
              reviews: payloadReviews,
            }
            : {}),
        },
        goodsInHand,
        // Cleared server-side on success: the purchase order is the durable
        // record now, and the draft's whole job was to survive until this
        // moment. A rejected confirm leaves it exactly where it was.
        draftId,
        countsAffirmed,
        receivedAs
      );
      setIdentityOpen(false);
      setResult(confirmed);
      setDraftId(null);
      setDraftFiles([]);
      setDrafts((prev) => prev.filter((d) => d.id !== draftId));
      setPhase('done');
      if (confirmed.partial_receive) {
        toast.info(
          `${confirmed.po_number} created. ${confirmed.received_line_count} of ${stockCount} lines went to stock.`
        );
      } else if (confirmed.goods_in_hand) {
        toast.success(`${confirmed.po_number} created and ${confirmed.received_line_count} lines stocked.`);
      } else {
        toast.success(`${confirmed.po_number} created. Receive it when the box lands.`);
      }
    } catch (err: any) {
      const receiverError = receiverErrorMessage(err);
      if (receiverError) {
        setIdentityError(receiverError);
        return;
      }
      setIdentityOpen(false);
      const body = err?.data?.error;
      setSubmitError(body?.message || err?.message || 'That scan could not be confirmed.');
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    setPhase('idle');
    setParsed(null);
    setResult(null);
    setLines([]);
    setSubmitError(null);
    setParseError(null);
    // The DRAFT IS NOT DISCARDED here, on purpose. "Scan a different file" and
    // "throw that scan away" are different intentions, and the second one has
    // its own button in the list below. Backing out of a review leaves the
    // reading exactly where it was, which is the whole promise of the holding
    // cell.
    setDraftId(null);
    setDraftFiles([]);
    clearPhotos();
    refreshDrafts();
  };

  // ── done ────────────────────────────────────────────────────────────────
  if (phase === 'done' && result) {
    return (
      <PageLayout kind="focus" className="pt-4 space-y-4">
        <h1 className="text-2xl font-bold text-primary">{result.po_number} is in</h1>
        <p className="text-sm text-on-surface-variant">
          {result.goods_in_hand
            ? `${result.received_line_count} line${result.received_line_count === 1 ? '' : 's'} went straight to stock at HQ.`
            : 'Nothing was received yet. The lines are waiting in the receiving hub.'}
          {(result.minted?.length ?? 0) > 0 &&
            ` ${result.minted.length} new item${result.minted.length === 1 ? '' : 's'} added to the catalog.`}
        </p>

        {result.partial_receive && (
          <Alert tone="warning" title="Some lines did not make it to stock.">
            The purchase order and its items are safe. Finish the rest from the receiving hub, nothing
            needs re-scanning.
          </Alert>
        )}

        <ListStack
          items={result.lines}
          getKey={(line) => String(line.line_number)}
          label="Lines on the order"
          renderRow={(line) => (
            <ListRow>
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="text-xs text-on-surface-variant w-4">{line.line_number}</span>
                <span className="w-[calc(100%-2rem)] sm:w-auto sm:flex-1 min-w-0 truncate text-on-surface">{line.description || '—'}</span>
                {line.sku && <span className="font-mono text-[11px] text-primary">{line.sku}</span>}
                <span className="text-xs text-on-surface-variant">{line.quantity}</span>
                {line.received ? (
                  <Tag size="sm" tone="good">{line.barcode}</Tag>
                ) : (
                  <Tag size="sm">{line.skipped_reason === 'not_stock' ? 'no goods' : 'not received'}</Tag>
                )}
              </span>
            </ListRow>
          )}
        />

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => history.push(`/procurement/po/${result.purchase_order_id}`)}
            className={buttonClass('filled')}
          >
            Open the purchase order
          </button>
          <button
            type="button"
            onClick={startOver}
            className={buttonClass('tonal')}
          >
            Scan another
          </button>
        </div>
      </PageLayout>
    );
  }

  // ── idle / parsing ──────────────────────────────────────────────────────
  if (phase !== 'review' || !doc || !extraction) {
    return (
      <PageLayout kind="focus" className="pt-4 space-y-4">
        <BackLink to="/procurement" label="Purchase Orders" />
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-primary">Scan a purchase order</h1>
          <HelpDrawer title="About scanning a purchase order">
            <p>
              For the box that just landed. Drop the purchase order that came with it, check what it
              read, and press Confirm: the order gets created, anything new gets added to the catalog,
              and the goods go straight to stock at HQ.
            </p>
            <p>
              Before anything stocks, the screen asks you to confirm the quantities are what
              actually arrived. The numbers come off the paper, and paper is sometimes wrong:
              count the goods, then tick the box. Nothing goes on a shelf uncounted.
            </p>
            <p>
              Turn off &quot;the goods are here&quot; if you are only filing the paperwork. The order is
              created either way and you can receive it later from the receiving hub.
            </p>
            <p className="text-on-surface font-medium">Items nobody has cataloged</p>
            <p>
              A line that matches nothing gets a short card asking for a SKU, a build stage
              and an item type. That is the quick way, and everything on the document is
              created together when you press Confirm.
            </p>
            <p>
              For a line that deserves a proper item, use &quot;Create with the wizard&quot;
              instead. It is the same form the parts catalog uses: a name, a kind, a
              manufacturer you can add on the spot. It creates the item straight away rather
              than waiting for Confirm, so the work is kept even if you put this document
              down.
            </p>
            <p className="text-on-surface font-medium">If the box is short</p>
            <p>
              Confirm receives every line in full, so it is for the shipment that arrived complete.
              If some of it is missing, turn off &quot;the goods are here&quot; and receive what did
              arrive from the receiving hub. Editing the quantity here would change what was
              ordered, and the rest would stop being owed to you.
            </p>
            <p>
              For older purchase orders in bulk, use Import history instead. That screen is built for
              many documents at once.
            </p>
          </HelpDrawer>
        </div>

        {/* ── photograph it ──────────────────────────────────────────────
            First, and biggest, because this is the one that happens standing
            at the receiving bench with the box open and the paperwork in the
            other hand. */}
        <Panel className="text-center">
          {/*
            THE ACCEPT LIST IS NARROW ON PURPOSE — see SCAN_PHOTO_ACCEPT in
            scanPhotos.ts. Widening it to image/* lets an iPhone hand over a
            HEIC file nobody downstream can read. `capture="environment"` opens
            the rear camera straight away on a phone and is ignored on desktop,
            where the same control is an ordinary file picker.
          */}
          <FilePick
            accept={SCAN_PHOTO_ACCEPT}
            capture="environment"
            multiple
            size="lg"
            className="w-full sm:w-auto"
            inputProps={{ id: photoInputId, 'aria-label': 'Photograph the purchase order' }}
            disabled={phase === 'parsing'}
            onFiles={acceptFiles}
          >
            {photos.length > 0 ? 'Add another page' : 'Photograph PO'}
          </FilePick>
          <p className="text-xs text-on-surface-variant mt-2.5">
            Good light, fill the frame, one page per photo.
          </p>
        </Panel>

        <Reveal show={photos.length > 0}>
          <div className="-m-1 space-y-2.5 p-1">
            <p className="text-xs text-on-surface-variant">
              {photos.length} page{photos.length === 1 ? '' : 's'}, in this order. Drag is not needed:
              use the arrows.
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="scan-photo-pages">
              {photos.map((photo, index) => (
                <li
                  key={photo.key}
                  className="overflow-hidden rounded-lg bg-surface-container-low"
                  data-testid={`scan-photo-${index + 1}`}
                >
                  <img
                    src={photo.url}
                    alt={`Page ${index + 1}`}
                    className="h-28 w-full bg-surface object-cover"
                  />
                  <div className="flex items-center justify-between gap-1 px-1.5 py-1">
                    <span className="text-[11px] text-on-surface-variant">Page {index + 1}</span>
                    <div className="flex items-center">
                      <Tooltip label={`Move page ${index + 1} earlier`}>
                        <IconButton
                          size="sm"
                          onClick={() => movePhoto(photo.key, -1)}
                          disabled={index === 0}
                          label={`Move page ${index + 1} earlier`}
                        >
                          <IconChevronLeft />
                        </IconButton>
                      </Tooltip>
                      <Tooltip label={`Move page ${index + 1} later`}>
                        <IconButton
                          size="sm"
                          onClick={() => movePhoto(photo.key, 1)}
                          disabled={index === photos.length - 1}
                          label={`Move page ${index + 1} later`}
                        >
                          <IconChevronRight className="h-3.5 w-3.5" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip label={`Remove page ${index + 1}`}>
                        <IconButton
                          size="sm"
                          onClick={() => removePhoto(photo.key)}
                          label={`Remove page ${index + 1}`}
                        >
                          <IconClose />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => parseFiles(photos.map((p) => p.file))}
                disabled={phase === 'parsing'}
                className={buttonClass('filled')}
              >
                {photos.length === 1 ? 'Read this page' : `Read these ${photos.length} pages`}
              </button>
              <button
                type="button"
                onClick={clearPhotos}
                className={buttonClass('tonal')}
              >
                Start the photos over
              </button>
            </div>
          </div>
        </Reveal>

        {/* ── or a file ────────────────────────────────────────────────── */}
        <DropZone
          what="the purchase order"
          hint="One .pdf or .xlsx, or photos of the pages."
          accept=".pdf,.xlsx,application/pdf,image/jpeg,image/png,image/webp"
          multiple
          disabled={phase === 'parsing'}
          onFiles={acceptFiles}
          inputProps={{ id: fileInputId, 'aria-label': 'Purchase order file' }}
        />

        <Reveal show={phase === 'parsing'}>
          <SkeletonGroup label="Reading it" className="space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </SkeletonGroup>
        </Reveal>

        <Alert show={!!parseError} tone="warning" live="alert">
          {parseError}
        </Alert>

        {/* ── waiting for review ────────────────────────────────────────
            The handoff point between the bench and the desk, so it sits on the
            idle screen where somebody arriving at a computer will see it
            without going looking. Communal: these are everyone's scans. */}
        <div className="space-y-2 border-t border-outline-variant pt-1">
          <h2 className="text-sm font-semibold text-on-surface">
            Waiting for review
            {drafts.length > 0 && <span className="text-on-surface-variant font-normal"> · {drafts.length}</span>}
          </h2>
          <ScanDraftsPanel
            drafts={drafts}
            busyId={draftBusyId}
            onReview={reviewDraft}
            onDiscard={discardDraft}
          />
        </div>
      </PageLayout>
    );
  }

  // ── review ──────────────────────────────────────────────────────────────
  // pb-44 on phone: the confirm bar stacks there and is taller than one row, so
  // the last line card would otherwise sit under it.
  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4">
    <div className="max-w-3xl space-y-4 pb-44 sm:pb-28">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-primary">Check what it read</h1>
        <button
          type="button"
          onClick={startOver}
          className={buttonClass('quiet', 'sm')}
        >
          Scan a different file
        </button>
      </div>
      <p className="text-xs text-on-surface-variant">
        {/* How sure the reader was about this document as a whole. Only on an
            AI reading: a spreadsheet has no opinion to report, and a green dot
            on one would claim a confidence nobody computed. */}
        {isAiSource(doc.source) && (
          <ConfidenceDot confidence={doc.confidence} className="mr-1.5 align-middle" />
        )}
        Read from {doc.source === 'photo_ai' ? 'your photos' : doc.source_filename}
        {isAiSource(doc.source) ? ' by the assistant' : ' straight off the spreadsheet'}.
      </p>

      {/* The paper this reading came from. Checking a part number against
          nothing is retyping, not reviewing — and by the time a desk sees this
          the box is closed and on a shelf. */}
      {draftId !== null && (
        <ScanDraftPages
          draftId={draftId}
          files={draftFiles}
          sourceFilename={doc.source_filename}
        />
      )}

      {/* ── header ─────────────────────────────────────────────────────── */}
      <Panel padding="sm" label="Purchase order header" className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="block">
            <span className={labelClass}>PO number</span>
            <Field
              ref={firstFieldRef}
              className="font-mono"
              warn={!!slotFor('po_number')?.ring}
              value={poNumber}
              onChange={(e) => {
                setPoNumber(e.target.value.toUpperCase());
                slotFor('po_number')?.markEdited();
              }}
              aria-label="PO number"
            />
            {slotFor('po_number') && <ReviewNote slot={slotFor('po_number')!} />}
          </label>
          <label className="block">
            <span className={labelClass}>Order date</span>
            <Field
              warn={!!slotFor('order_date')?.ring}
              type="date"
              value={orderDate}
              onChange={(e) => {
                setOrderDate(e.target.value);
                slotFor('order_date')?.markEdited();
              }}
              aria-label="Order date"
            />
            {slotFor('order_date') && <ReviewNote slot={slotFor('order_date')!} />}
          </label>
          <div>
            <span className={labelClass}>Lines</span>
            <p className="text-sm text-on-surface py-1.5">
              {includedLines.length}
              <Appear show={mintCount > 0} className="text-primary"> · {mintCount} new</Appear>
            </p>
            {slotFor('lines') && <ReviewNote slot={slotFor('lines')!} />}
          </div>
          <div>
            <span className={labelClass}>Total</span>
            <p className="text-sm text-on-surface py-1.5">{formatCents(linesTotalCents)}</p>
            <Reveal show={printedSubtotal !== null && printedSubtotal !== linesTotalCents}>
              <p className="text-[10px] text-amber-300/90">
                The document says {printedSubtotal === null ? '' : formatCents(printedSubtotal)}. That figure is kept, and the
                difference is noted on the order.
              </p>
            </Reveal>
            {slotFor('subtotal_cents') && <ReviewNote slot={slotFor('subtotal_cents')!} />}
          </div>
        </div>

        {/* Everything else the document carried. Read-only on purpose: this flow
            does not ask anyone to edit terms while holding a box, but the gate
            can flag any of them, and affirming a value the screen never showed
            is not a review. */}
        {DOC_FIELDS.some((f) => f.value) && (
          <div className="flex flex-wrap gap-x-6 gap-y-1.5 border-t border-outline-variant pt-1">
            {DOC_FIELDS.filter((f) => f.value || slotFor(f.field)).map((f) => (
              <div key={f.field} className="text-xs">
                <span className={labelClass}>{f.label}</span>
                <span className="block text-on-surface-variant">{f.value || '—'}</span>
                {slotFor(f.field) && <ReviewNote slot={slotFor(f.field)!} />}
              </div>
            ))}
          </div>
        )}

        {/* vendor */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {/* Two answers, one list: an existing vendor, or the create row that
              opens the two fields beside it. The row names the vendor from the
              DOCUMENT rather than the editable field below, so the picker does
              not rewrite itself under someone typing that name. */}
          <div className="block">
            <EntityCombobox
              label="Vendor"
              labelClassName={labelClass}
              inputClassName={fieldClass()}
              value={vendorId || CREATE_NEW_VENDOR}
              options={vendorPickerOptions}
              onSelect={(o) => setVendorId(o && o.id !== CREATE_NEW_VENDOR ? String(o.id) : '')}
              emptyMessage="No vendor matches that. Clear the box to create a new one."
            />
            <Reveal show={!!doc.vendor_resolution?.matched_vendor_id && vendorId === doc.vendor_resolution.matched_vendor_id}>
              <p className="text-[10px] text-on-surface-variant mt-1">
                Matched from &quot;{doc.vendor_resolution?.document_name}&quot; on the document.
              </p>
            </Reveal>
          </div>

          <Reveal show={!vendorMatched}>
            <div className="-m-1 grid grid-cols-1 gap-2 p-1 sm:grid-cols-2">
              <label className="block">
                <span className={labelClass}>New vendor name</span>
                <Field
                  value={vendorCreateName}
                  onChange={(e) => setVendorCreateName(e.target.value)}
                  // purchase_order_lines.vendor is varchar(100) and takes this
                  // name verbatim; past that the insert is a driver error, not a
                  // sentence.
                  maxLength={100}
                  aria-label="New vendor name"
                />
              </label>
              <label className="block">
                <span className={labelClass}>Vendor type</span>
                <Select
                  label="Vendor type"
                  placeholder="Select…"
                  value={vendorCreateType || null}
                  onChange={setVendorCreateType}
                  options={[{ key: '', label: 'Select…' }, ...vendorTypes.map((t) => ({ key: t.key, label: t.label }))]}
                />
              </label>
            </div>
          </Reveal>
        </div>
      </Panel>

      {/* ── the other product's purchase order ─────────────────────────── */}
      {/* Above the review gate on purpose: it decides whether this document
          belongs here at all, which is a bigger question than any field on it.
          And this lane RECEIVES, so a yes here puts a box on a shelf.
          Neutral livery, not a color: the amber below means "the reader was not
          sure" and reusing it would say the model doubted something. A hue of its
          own is worse still, because Swarm's brand accent IS a muted blue
          (instance.ts), so a blue "Firefleet" chip on the Swarm build would paint
          the foreign product in the local product's color. The red blocked chip
          already carries the to-do; this panel carries the fact. */}
      <Reveal show={!!(foreignEntity && parsed)}>
      {foreignEntity && parsed && (
        <Panel on="ground" padding="sm" label={`${PRODUCT_LABEL[foreignEntity]} raised this order`} className="space-y-2">
          <p className="text-sm font-medium text-on-surface">
            {PRODUCT_LABEL[foreignEntity]} raised this order
          </p>
          {/* The server's sentence when the staged number is still the one on
              screen. Only the server knows whether this tail falls in the range the
              other product's app issues from, which decides between "nothing there
              holds it" and "go and check". Once the reviewer edits the number that
              answer may be stale, so fall back to the part that is always true. */}
          <p className="text-xs text-on-surface-variant">
            {(poNumber.trim() === (doc?.extraction?.po_number || '') && foreignWarning)
              || `${poNumber.trim()} was raised by ${PRODUCT_LABEL[foreignEntity]}, and the box is here. `
                + `Confirm and this order and its stock are tracked in ${PRODUCT_LABEL[parsed.instance]}. `
                + `Check it is not already open in ${PRODUCT_LABEL[foreignEntity]}.`}
          </p>
          <Checkbox
            className="text-xs"
            checked={foreignAck}
            onChange={(e) => setForeignAck(e.target.checked)}
            label={`Yes, track ${poNumber.trim()} in ${PRODUCT_LABEL[parsed.instance]}.`}
          />
        </Panel>
      )}
      </Reveal>

      {/* ── the needs-review gate ──────────────────────────────────────── */}
      {/* Only what has no home of its own above: document-level doubts and
          line-level ones. Every header field the gate can raise is anchored
          beside its own value, so nothing here is a blind affirmation. */}
        <Alert
          show={reviewSlots.length > 0}
          tone={unreviewed === 0 ? 'success' : 'warning'}
          live={false}
          title={unreviewed === 0
            ? 'Everything flagged has been looked at.'
            : `${unreviewed} thing${unreviewed === 1 ? '' : 's'} the reader was not sure of`}
        >
          <div className="mt-2 space-y-2">
            {looseSlots.map((slot) => (
              <div key={slot.key} className="text-xs text-on-surface-variant">
                <span className="text-on-surface-variant">{slot.label}: </span>
                <ReviewNote slot={slot} />
              </div>
            ))}
            {looseSlots.length < reviewSlots.length && (
              <p className="text-[11px] text-on-surface-variant">
                The rest are marked on the fields above.
              </p>
            )}
          </div>
        </Alert>

      {/* ── lines ──────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        {lines.map((line) => (
          <PoScanLineCard
            key={line.key}
            line={line}
            error={errorsByKey.get(line.key) || null}
            onPatch={(patch) => patchLine(line.key, patch)}
            onPatchMint={(patch) => patchMint(line.key, patch)}
            onSwitchToMint={() => switchToMint(line.key)}
            onSwitchToMatch={() => switchToMatch(line.key)}
            codedVendors={codedVendors}
            onNewVendor={() => setVendorDrawerFor(line.key)}
          />
        ))}
      </div>

      <Alert show={!!submitError} tone="error" live="alert">
        {submitError}
      </Alert>

      {/* ── the one press ──────────────────────────────────────────────── */}
      {/* The affirmations and the blocker sentence stay here: they are this
          lane's, and this is the only gate where saying yes to the goods
          changes what Confirm does. The bar owns the arrangement, and its
          `fixed` mode is what the page's pb-44 sm:pb-28 makes room for. */}
      <ConfirmBar
        mode="fixed"
        presentation="prose"
        blockers={blockers.length > 0 ? [`Still needs ${blockers.join(', ')}.`] : []}
        label={goodsInHand ? 'Confirm and stock it' : 'Create the purchase order'}
        busyLabel="Working…"
        busy={submitting}
        disabled={!canConfirm}
        onConfirm={handleConfirm}
        aside={
          <div className="flex flex-col gap-2">
            <Checkbox
              checked={goodsInHand}
              onChange={(e) => setGoodsInHand(e.target.checked)}
              label="The goods are here"
              description={goodsInHand
                ? `On Confirm, all ${stockCount} goods line${stockCount === 1 ? '' : 's'} are received in full into HQ.`
                : 'Only the purchase order is created. Receive it later from the receiving hub.'}
            />
            {/* B7: the numbers above go into stock as they read, so stocking
                asks for a human count first. The API refuses without it, so
                this box is a statement, not decoration. */}
            <Reveal show={goodsInHand}>
              <div className="-m-1 p-1">
              <Checkbox
                className="sm:ml-6"
                checked={countsAffirmed}
                onChange={(e) => setCountsAffirmed(e.target.checked)}
                label="These quantities are what actually arrived"
                description="Count the goods, not the paper. The numbers above go into stock exactly as they read."
              />
              </div>
            </Reveal>
          </div>
        }
      />

      {identityOpen && (
        <ReceiverIdentityModal
          busy={submitting}
          error={identityError}
          onConfirm={({ user_id, pin }) => {
            setIdentityError(null);
            submitConfirm({ user_id, pin });
          }}
          onClose={() => {
            setIdentityOpen(false);
            setIdentityError(null);
          }}
        />
      )}

      {/* ── a maker nobody has entered ─────────────────────────────────────── */}
      {/* One drawer for the page, opened from any board card's "New vendor…".
          Same reasoning as QuoteDraftPage: a card in a list of thirty is
          nowhere to put a form, and the document's vendor above is who we
          BOUGHT from, which is not always who built the board. The saved vendor
          joins both lists: the cards' manufacturer segment, and the document's
          own vendor picker, since on a maker's invoice it is both. */}
      {vendorDrawerFor && (
        <VendorForm
          mode="create"
          types={vendorTypes}
          onClose={() => setVendorDrawerFor(null)}
          onSaved={(saved?: VendorFullRow) => {
            const forLine = vendorDrawerFor;
            setVendorDrawerFor(null);
            if (!saved) return;
            // Offered as the DOCUMENT's vendor straight away, code or no code:
            // the row is in the database, and this page's one vendor fetch
            // happened before it existed.
            setVendorList((prev) =>
              prev.some((v) => v.id === saved.id)
                ? prev
                : [...prev, {
                  id: saved.id,
                  name: saved.name,
                  type: saved.type,
                  is_disabled: !!saved.is_disabled,
                }]
            );
            if (!saved.code) {
              toast.error(
                `${saved.name} was created, but a board code carries a two-letter vendor code `
                + 'and this one has none. Add one under Vendors, then pick it here.'
              );
              return;
            }
            const code = saved.code.toUpperCase();
            setAddedVendors((prev) =>
              prev.some((v) => v.id === saved.id)
                ? prev
                : [...prev, { id: saved.id, code, name: saved.name, is_disabled: !!saved.is_disabled }]
            );
            setLines((prev) =>
              prev.map((l) =>
                l.key === forLine && l.mint?.board
                  ? { ...l, mint: { ...l.mint, ...boardMint({ ...l.mint.board, vendorCode: code }) } }
                  : l
              )
            );
          }}
        />
      )}
    </div>
    </PageLayout>
  );
};

export default PoScanPage;
